import React, { useState } from 'react';

// Access gate for the provisioning pages. The entered code is the backend's provisioning key
// (Provisioning:AccessKey): it is verified by the API and sent as X-Provisioning-Key on every
// provisioning call, so nothing secret is baked into this bundle.
const API_BASE = process.env.REACT_APP_API_BASE ?? '';
const KEY_STORAGE = 'provisioning-key';

/** fetch() for provisioning endpoints: adds the entered key; on 401 forgets it and re-shows the gate. */
export async function provisioningFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('X-Provisioning-Key', sessionStorage.getItem(KEY_STORAGE) ?? '');
  const resp = await fetch(url, { ...init, headers });
  if (resp.status === 401) {
    sessionStorage.removeItem(KEY_STORAGE);
    window.location.reload();
  }
  return resp;
}

export default function ProvisioningGate({ children }: { children: React.ReactNode }) {
  const [unlocked, setUnlocked] = useState(() => !!sessionStorage.getItem(KEY_STORAGE));
  const [entry, setEntry] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  if (unlocked) return <>{children}</>;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setChecking(true);
    try {
      const resp = await fetch(`${API_BASE}/provision/access-check`, {
        headers: { 'X-Provisioning-Key': entry },
      });
      if (resp.ok) {
        sessionStorage.setItem(KEY_STORAGE, entry);
        setUnlocked(true);
      } else {
        setError(resp.status === 401 ? 'Incorrect code.' : `Server error (HTTP ${resp.status}).`);
      }
    } catch {
      setError('Could not reach the server.');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        background: 'linear-gradient(180deg, darkblue, blue)',
      }}
    >
      <form
        onSubmit={submit}
        style={{
          width: 'min(22rem, 100%)',
          display: 'grid',
          gap: '1rem',
          padding: '1.75rem',
          borderRadius: 14,
          background: 'rgba(0,0,0,0.35)',
          border: '1px solid rgba(255,255,255,0.18)',
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <h1 style={{ margin: 0, fontSize: '1.4rem' }}>Provisioning</h1>
        <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)' }}>Enter the access code to continue.</p>
        <input
          type="password"
          value={entry}
          onChange={(e) => { setEntry(e.target.value); setError(null); }}
          autoFocus
          aria-label="Access code"
          style={{
            padding: '0.65rem 0.8rem',
            borderRadius: 8,
            border: error ? '1px solid #ff8a96' : '1px solid rgba(255,255,255,0.3)',
            background: 'rgba(255,255,255,0.9)',
            color: '#111',
            fontSize: '1rem',
          }}
        />
        {error && <p style={{ margin: 0, color: '#ff8a96' }}>{error}</p>}
        <button
          type="submit"
          disabled={checking || !entry}
          style={{
            padding: '0.65rem 1rem',
            borderRadius: 8,
            border: '1px solid #95ffc5',
            background: 'transparent',
            color: '#95ffc5',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {checking ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}
